import React, { useState, useMemo, useContext, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Progress } from './ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
  Play,
  Save,
  Settings,
  Shield,
  FileText,
  Zap,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Copy,
  Download,
  Upload,
  RotateCcw,
  Bookmark,
  BookmarkPlus,
  Trash2,
  Eye,
  EyeOff,
  Code,
  Database,
  Brain,
  Target,
  BarChart3,
  Activity,
  Layers,
  GitCompare,
  MessageSquare,
  Monitor,
  Plus,
  ChevronDown,
  ChevronUp,
  Check,
  X
} from 'lucide-react';
import { Layout } from './Layout';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { toBase64 } from '../src/Utils/authUtils';
import { apiClient } from '../src/Utils/apiClient';
import { useModelThinkingConfig } from '../src/hooks/useModelThinking';
import { AppContext } from '../src/App';

// Mock AI Models
const AI_MODELS = [
  { id: 'gemini 2.0', name: 'gemini 2.0', provider: 'OpenAI', description: 'Latest multimodal model' },
  { id: 'gemini 2.5', name: 'gemini 2.5', provider: 'OpenAI', description: 'High performance model' },
  // { id: 'claude-3-sonnet', name: 'Claude 3 Sonnet', provider: 'Anthropic', description: 'Balanced performance' },
  // { id: 'claude-3-opus', name: 'Claude 3 Opus', provider: 'Anthropic', description: 'Most capable model' },
  // { id: 'gemini-pro', name: 'Gemini Pro', provider: 'Google', description: 'Advanced reasoning' }
];

// Guardrail Categories
const GUARDRAIL_CATEGORIES = [
  { id: 'harassment', name: 'Harassment', description: 'Content that harasses, bullies, or threatens individuals' },
  { id: 'hate', name: 'Hate Speech', description: 'Content that promotes hatred against groups' },
  { id: 'sexual', name: 'Sexual Content', description: 'Sexually explicit or suggestive content' },
  { id: 'violence', name: 'Violence', description: 'Content depicting or promoting violence' },
  { id: 'self-harm', name: 'Self-Harm', description: 'Content encouraging self-injurious behavior' },
  { id: 'illegal', name: 'Illegal Activities', description: 'Content promoting illegal activities' }
];

interface RunConfiguration {
  model: string;
  temperature: number;
  thinkingMode: boolean;
  thinkingLevel: string;
  thinkingBudget: number;
  systemPrompt: string;
  guardrails: Record<string, boolean>;
  structuredOutput: boolean;
  // prompt?: string;
  // chartData?: string;
  // expectedOutput?: string;

}

interface RunResult {
  id: string;
  modelId: string;
  modelName: string;
  output: string;
  structured?: any;
  duration: number;
  timestamp: string;
  tokensUsed: number;
  cost: number;
  status: 'success' | 'error' | 'warning';
  errors?: string[];
  warnings?: string[];
  executionLog: string[];
}

export function TurfAIStudio() {
  // Configuration State
  const [config, setConfig] = useState<RunConfiguration>({
    model: '',
    temperature: 0,
    thinkingMode: false,
    thinkingLevel: '',
    thinkingBudget: 0,
    systemPrompt: '',
    guardrails: Object.fromEntries(GUARDRAIL_CATEGORIES.map(cat => [cat.id, true])),
    structuredOutput: false
  });

  const { token } = useAuth();

  const {
    config: modelThinkingConfig,
    loading: modelThinkingLoading,
    error: modelThinkingError,
  } = useModelThinkingConfig(config.model || '', token);

  const modelThinkingReady = !!modelThinkingConfig && !modelThinkingLoading && !modelThinkingError;
  const modelThinkingModeEnabled = modelThinkingConfig?.thinkingModeEnabled ?? false;
  const normalizedThinkingLevels =
    Array.isArray(modelThinkingConfig?.thinkingLevelOptions)
      ? modelThinkingConfig!.thinkingLevelOptions!
          .map((opt) => String(opt).toLowerCase())
          .filter((opt) => ['minimal', 'low', 'medium', 'high'].includes(opt))
      : null;

  const apiBudgetMin = modelThinkingConfig?.budgetRange?.min;
  const apiBudgetMax = modelThinkingConfig?.budgetRange?.max;
  const effectiveBudgetMin = typeof apiBudgetMin === 'number' ? apiBudgetMin : 1000;
  const effectiveBudgetMax = typeof apiBudgetMax === 'number' ? apiBudgetMax : 24576;

  const isBudgetNumeric =
    modelThinkingConfig?.thinkingBudgetType === 'numeric' &&
    !!modelThinkingConfig?.budgetRange &&
    typeof modelThinkingConfig.budgetRange.min === 'number' &&
    typeof modelThinkingConfig.budgetRange.max === 'number' &&
    effectiveBudgetMin <= effectiveBudgetMax;

  const modelBudgetMin = effectiveBudgetMin;
  const modelBudgetMax = effectiveBudgetMax;

  const thinkingUiLocked =
    modelThinkingLoading || !!modelThinkingError || !modelThinkingConfig;
  const [promptsList, setPromptList] = useState<{ id: string; name: string }[]>([])

  // Comparison Configuration
  const [comparisonModel, setComparisonModel] = useState<string>('claude-3-sonnet');
  const [comparisonEnabled, setComparisonEnabled] = useState(false);

  // UI State for collapsible sections
  const [guardrailsExpanded, setGuardrailsExpanded] = useState(false);
  const [executionLogExpanded, setExecutionLogExpanded] = useState<Record<string, boolean>>({});

  // Thinking budget as string so user can clear and type (validated on blur / submit)
  const [thinkingBudgetInput, setThinkingBudgetInput] = useState('');
  const [thinkingBudgetError, setThinkingBudgetError] = useState<string | null>(null);
  const BUDGET_MIN = 1000;
  const BUDGET_MAX = 24576;

  // If the selected model doesn't support thinking, force-disable it.
  useEffect(() => {
    if (!modelThinkingReady) return;
    if (modelThinkingModeEnabled) return;
    if (config.thinkingMode) {
      setConfig((prev) => ({ ...prev, thinkingMode: false }));
    }
  }, [modelThinkingReady, modelThinkingModeEnabled]);

  // Keep thinkingLevel in sync with the model's supported options.
  useEffect(() => {
    if (!modelThinkingReady) return;
    if (!normalizedThinkingLevels || normalizedThinkingLevels.length === 0) {
      // Model has no thinking level support – clear the value.
      if (config.thinkingLevel !== '') {
        setConfig((prev) => ({ ...prev, thinkingLevel: '' }));
      }
      return;
    }
    if (!normalizedThinkingLevels.includes(config.thinkingLevel)) {
      setConfig((prev) => ({ ...prev, thinkingLevel: normalizedThinkingLevels[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelThinkingReady, normalizedThinkingLevels?.join('|')]);

  // Combined Input State (single field for prompt, input, expected output)
  const [combinedInput, setCombinedInput] = useState('');

  // Run State
  const [isRunning, setIsRunning] = useState(false);
  const [runProgress, setRunProgress] = useState(0);
  const [results, setResults] = useState<RunResult[]>([]);

  // File upload state
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState([]);

  // Copy button state for visual feedback
  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});

  // Add preset management state
  const [presets, setPresets] = useState<any[]>([]);
  const [showPresetDialog, setShowPresetDialog] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [newPresetDescription, setNewPresetDescription] = useState('');
  const { sideBarItems } = useContext(AppContext);
  const turfAiMenu = sideBarItems.find((item: any) => item.name === "Turf AI Studio");

  const hasPermission = (permission: string) => {
    return turfAiMenu?.userPermissions?.includes(permission);
  }

  // Add refs for select all functionality
  const promptTextareaRef = useRef<HTMLTextAreaElement>(null);
  const aiOutputRefs = useRef<{ [key: string]: HTMLDivElement }>({});
  // get prompt and chart_data
  const getPromptAndChartData = (input: string) => {

    const promptMatch = input.match(/prompt\s*:\s*([\s\S]*?)(?=\s*(?:chart[_ ]?data\s*:|expected[_ ]?output\s*:|$))/i);
    const chartMatch = input.match(/chart[_ ]?data\s*:\s*([\s\S]*?)(?=\s*(?:expected[_ ]?output\s*:|$))/i);
    const outputMatch = input.match(/expected[_ ]?output\s*:\s*([\s\S]*?)(?=$)/i);

    const result = {
      prompt: promptMatch ? promptMatch[1]?.trim() : null,          // required
      chartData: chartMatch ? chartMatch[1]?.trim() : null,        // optional
      expectedOutput: outputMatch ? outputMatch[1]?.trim() : null, // optional
    };
    // console.log(result,"nvfnvf")
    // setConfig(prevConfig => ({
    //   ...prevConfig,
    //   prompt: result?.prompt||'',
    //   chartData: result?.chartData||'',
    //   expectedOutput: result?.expectedOutput||'',
    // }))
    return result;

  }

  // Handle select all functionality for prompt textarea
  const handlePromptKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
      e.preventDefault();
      const textarea = promptTextareaRef.current;
      if (textarea) {
        textarea.select();
        textarea.setSelectionRange(0, textarea.value.length);
      }
    }
  };

  // Handle select all functionality for AI output areas
  const handleOutputKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, resultId: string) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
      e.preventDefault();
      const outputDiv = aiOutputRefs.current[resultId];
      if (outputDiv) {
        // Create a selection for the entire content
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(outputDiv);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  };

  // Helper function to show copy feedback
  const showCopyFeedback = (buttonId: string) => {
    setCopiedStates(prev => ({ ...prev, [buttonId]: true }));
    setTimeout(() => {
      setCopiedStates(prev => ({ ...prev, [buttonId]: false }));
    }, 2000); // Show check mark for 2 seconds
  };

  // Computed Values
  const selectedModel = promptsList?.find(m => m.id === config.model) ?? AI_MODELS.find(m => m.id === config.model);
  const selectedComparisonModel = AI_MODELS.find(m => m.id === comparisonModel);
  const canRun = combinedInput.trim().length > 0;

  // Handlers
  const handleConfigChange = (key: keyof RunConfiguration, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };
  const getModel = async () => {
    try {
      const response = await apiClient('prompts/ai/models', {
        method: 'GET',
        token
      })
      const data = await response.json()
      if (!data?.success) {
        throw new Error(data?.error || 'Failed to fetch models');
      }
      setPromptList(data?.data)
      // console.log(data)
    }
    catch (error: any) {
      toast.error('Failed to fetch models');
    }

  }
  useEffect(() => {
    getModel()
  }, [])
  useEffect(() => {
    if (promptsList?.length > 0 && !promptsList.some(m => m.id === config.model)) {
      handleConfigChange('model', promptsList[0]?.id ?? '');
    }
  }, [promptsList]);
  useEffect(() => {
    if (config.thinkingMode) {
      // Always start empty when thinking mode is turned on so the user must enter a value.
      setThinkingBudgetInput('');
      setThinkingBudgetError(null);
    }
  }, [config.thinkingMode, isBudgetNumeric, modelBudgetMin, modelBudgetMax]);
  const handleGuardrailChange = (categoryId: string, enabled: boolean) => {
    setConfig(prev => ({
      ...prev,
      guardrails: { ...prev.guardrails, [categoryId]: enabled }
    }));
  };

  // File upload handler
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    //  setFile([...event.target?.files || []]);
    // Check file size (limit to 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File too large', {
        description: 'Please select a file smaller than 10MB'
      });
      return;
    }

    // Check file type
    const allowedTypes = [
      // 'text/plain',
      'application/json',
      // 'text/markdown',
      // 'text/csv',
      // 'application/xml',
      // 'text/xml',
      // 'application/yaml',
      // 'text/yaml',
      // 'text/x-yaml'
    ];

    // const allowedExtensions = ['.txt', '.json', '.md', '.csv', '.xml', '.yaml', '.yml'];
    const allowedExtensions = ['.json']
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error('Unsupported file type', {
        description: 'Please upload a text file ( .json)'
      });
      return;
    }

    setIsUploading(true);
    // const convertedFile= await toBase64(event.target?.files?.[0])
    // setFile(prev=>({...prev,
    //   file: Array.from(event.target?.files || []),
    // base64Files:[convertedFile]})
    // );
    setFile([...event.target.files || []])
    setIsUploading(false);
    toast.success('File uploaded successfully', {
      description: `Loaded ${file.name} (${(file.size / 1024).toFixed(1)} KB)`
    });

    // const reader = new FileReader();
    // reader.onload = (e) => {
    //   try {
    //     const content = e.target?.result as string;
    //     if (content) {
    //       // setCombinedInput(content);
    //       toast.success('File uploaded successfully', {
    //         description: `Loaded ${file.name} (${(file.size / 1024).toFixed(1)} KB)`
    //       });
    //     } else {
    //       toast.error('Failed to read file', {
    //         description: 'The file appears to be empty or corrupted'
    //       });
    //     }
    //   } catch (error) {
    //     toast.error('Failed to process file', {
    //       description: 'An error occurred while reading the file'
    //     });
    //   } finally {
    //     setIsUploading(false);
    //     // Reset the input so the same file can be uploaded again
    //     event.target.value = '';
    //   }
    // };

    // reader.onerror = () => {
    //   toast.error('Failed to read file', {
    //     description: 'An error occurred while reading the file'
    //   });
    //   setIsUploading(false);
    //   event.target.value = '';
    // };

    // reader.readAsText(file);
  };

  // Copy to clipboard handler
  const handleCopyToClipboard = async () => {
    const buttonId = 'input-copy';
    try {
      await navigator.clipboard.writeText(combinedInput);
      showCopyFeedback(buttonId);
      toast.success('Copied to clipboard', {
        description: `${combinedInput.length} characters copied`
      });
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      try {
        const textArea = document.createElement('textarea');
        textArea.value = combinedInput;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        showCopyFeedback(buttonId);
        toast.success('Copied to clipboard', {
          description: `${combinedInput.length} characters copied`
        });
      } catch (fallbackError) {
        toast.error('Failed to copy', {
          description: 'Unable to copy to clipboard. Please try selecting and copying manually.'
        });
      }
    }
  };

  // Enhanced copy handler for AI output
  const handleCopyOutput = async (content: string, resultId?: string) => {
    const buttonId = resultId ? `result-${resultId}` : 'output-copy';
    try {
      await navigator.clipboard.writeText(JSON.stringify(content, null, 2));
      showCopyFeedback(buttonId);
      toast.success('Output copied to clipboard', {
        description: `${content.length} characters copied successfully`
      });
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      try {
        const textArea = document.createElement('textarea');
        textArea.value = JSON.stringify(content, null, 2);
        textArea.style.position = 'fixed';
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        textArea.style.padding = '0';
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        textArea.style.background = 'transparent';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (successful) {
          showCopyFeedback(buttonId);
          toast.success('Output copied to clipboard', {
            description: `${content.length} characters copied successfully`
          });
        } else {
          throw new Error('Copy command failed');
        }
      } catch (fallbackError) {
        toast.error('Failed to copy output', {
          description: 'Unable to copy to clipboard. Please try selecting and copying manually.'
        });
      }
    }
  };

  // Download handler for AI output
  const handleDownloadOutput = (content: string, modelName: string, resultId: string) => {
    try {
      // Detect if content is JSON
      const isJSON = isValidJSON(content);

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
      const timeStr = new Date().toISOString().split('T')[1].split('.')[0].replace(/:/g, '-');
      const extension = isJSON ? '.json' : '.md';
      const filename = `turf-ai-output_${modelName.replace(/\s+/g, '-').toLowerCase()}_${timestamp}_${timeStr}${extension}`;

      // Prepare content for download
      let downloadContent = content;

      if (isJSON) {
        // Pretty format JSON
        try {
          const jsonObject = JSON.parse(content);
          downloadContent = JSON.stringify(jsonObject, null, 2);
        } catch (e) {
          // If parsing fails, use original content
          downloadContent = content;
        }
      } else {
        // Add header for markdown files
        const header = `# TurfAI Studio Output\n\n**Model:** ${modelName}\n**Generated:** ${new Date().toLocaleString()}\n**Result ID:** ${resultId}\n\n---\n\n`;
        downloadContent = header + JSON.stringify(content, null, 2);
      }

      // Create blob and download
      const blob = new Blob([downloadContent], {
        type: isJSON ? 'application/json' : 'text/markdown'
      });
      const url = URL.createObjectURL(blob);

      // Create temporary download link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Output downloaded successfully', {
        description: `Saved as ${filename}`
      });
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download output', {
        description: 'An error occurred while preparing the download. Please try again.'
      });
    }
  };

  // Helper function to detect and format JSON
  const isValidJSON = (str: string): boolean => {
    try {
      if (typeof str !== 'string') return false
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  };

  // Component to render formatted output
  const FormattedOutput = ({ content }: { content: string }) => {
    if (isValidJSON(content)) {
      try {
        const jsonObject = JSON.parse(content);
        return (
          <pre className="text-xs font-mono whitespace-pre-wrap overflow-auto">
            {JSON.stringify(jsonObject, null, 2)}
          </pre>
        );
      } catch {
        // Fallback to regular text if parsing fails
        return (
          <pre className="text-sm whitespace-pre-wrap font-mono">{content}</pre>
        );
      }
    }

    // Regular text output
    return (
      <pre className="text-sm whitespace-pre-wrap font-mono">{JSON.stringify(content, null, 2)}</pre>
    );
  };

  const handleRunAudit = async () => {
    if (!canRun) return;
    setIsRunning(true);
    setRunProgress(0);
    setResults([]);

    const modelsToRun = comparisonEnabled ? [config.model, comparisonModel] : [config.model];
    const newResults: RunResult[] = [];

    for (let i = 0; i < modelsToRun.length; i++) {
      const modelId = modelsToRun[i];
      // const model = AI_MODELS.find(m => m.id === modelId);

      // if (!model) continue;

      // Simulate AI processing with progress
      const startTime = Date.now();
      // const executionLog = [
      //   `[${new Date().toISOString()}] Starting execution with ${model.name}`,
      //   `[${new Date().toISOString()}] Parsing input prompt and data`,
      //   `[${new Date().toISOString()}] Applying safety guardrails`,
      //   `[${new Date().toISOString()}] Sending request to ${model.provider} API`,
      // ];

      setRunProgress((i / modelsToRun.length) * 50);
      const data = getPromptAndChartData(combinedInput);

      let budgetToSend: number | undefined;
      if (config?.thinkingMode && isBudgetNumeric) {
        const raw = thinkingBudgetInput.trim();
        if (raw === '') {
          toast.error('Please enter a thinking budget');
          setIsRunning(false);
          return;
        }
        const parsed = parseInt(raw, 10);
        if (Number.isNaN(parsed)) {
          toast.error('Invalid thinking budget', { description: 'Enter a valid number' });
          setIsRunning(false);
          return;
        }
        if (parsed < modelBudgetMin || parsed > modelBudgetMax) {
          toast.error(`Please enter a value between ${modelBudgetMin.toLocaleString()} and ${modelBudgetMax.toLocaleString()}`);
          setIsRunning(false);
          return;
        }
        budgetToSend = parsed;
      }

      const formData = new FormData();
      formData.append('prompt', data?.prompt || '');
      formData.append('temperature', config?.temperature || '0');
      formData.append('modelType', config?.model || '');
      formData.append('thinkingMode', config?.thinkingMode ? 'true' : 'false');
      // Only send thinkingLevel when the model supports level-based thinking.
      if (normalizedThinkingLevels && config.thinkingLevel) {
        formData.append('thinkingLevel', config.thinkingLevel);
      }
      // Only send thinkingBudget when the model uses a numeric budget and a value was entered.
      if (isBudgetNumeric && budgetToSend !== undefined) {
        formData.append('thinkingBudget', String(budgetToSend));
      }

      if (file && file[0]) {
        formData.append('chartFile', file[0]);
      } else {
        formData.append('chartData', data?.chartData || '');
      }
      if (data?.expectedOutput) {
        formData.append('expectedOutput', data?.expectedOutput || '');
      }


      if (!data?.prompt) {
        toast.error('Please enter prompt', {
          description: 'Prompt is required'
        })
        setIsRunning(false);
        return;
      }
      if (!data?.chartData && !file[0]) {

        toast.error('Please provide chart Data', {
          description: 'For Chart Data either provide chart file or chart data'
        })
        setIsRunning(false);
        return;
      }


      try {
        // Simulate API call
        const result = await fetch(`${import.meta.env.VITE_API_URL}prompts/turf-ai-studio/prompt/test`, {
          method: 'POST',
          headers: {
            // 'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        if (!result.ok) {
          throw new Error(`Server returned ${result.status}: ${result.statusText}`);
        }
        const response = await result.json();

        // await new Promise(resolve => setTimeout(resolve, 2000 + Math.random() * 2000));

        // executionLog.push(`[${new Date().toISOString()}] Received response from ${model.provider}`);
        // executionLog.push(`[${new Date().toISOString()}] Processing and validating output`);

        // Mock result generation
        const mockResult: RunResult = {
          id: `run_${Date.now()}_${modelId}`,
          modelId,
          modelName: response?.data?.model,
          output: response?.data?.content,
          // structured: config.structuredOutput ? generateMockStructuredOutput(modelId) : undefined,
          duration: (Date.now() - startTime) / 1000,
          timestamp: new Date().toISOString(),
          tokensUsed: response?.data?.tokensUsed ?? 0,
          cost: response?.data?.cost ?? 0,
          status: 'success',
          executionLog: [],
          // warnings: Math.random() > 0.7 ? ['Minor formatting inconsistency detected'] : undefined,
        };

        newResults.push(mockResult);
        setResults([...newResults]);
        toast.success('AI audit completed', {
          description: `Prompt test executed successfully`
        });

      } catch (error) {
        if (error) {

          toast.error('AI audit failed', {
            description: `Prompt test failed`
          });
        }
        // executionLog.push(`[${new Date().toISOString()}] Error: ${error}`);

        // const errorResult: RunResult = {
        //   id: `run_${Date.now()}_${modelId}`,
        //   modelId,
        //   modelName: model.name,
        //   output: '',
        //   duration: (Date.now() - startTime) / 1000,
        //   timestamp: new Date().toISOString(),
        //   tokensUsed: 0,
        //   cost: 0,
        //   status: 'error',
        //   errors: ['Failed to process input data', 'Invalid response format detected'],
        //   executionLog: [...executionLog, `[${new Date().toISOString()}] Execution failed`]
        // };

        // newResults.push(errorResult);
        // setResults([...newResults]);
      }

      setRunProgress(((i + 1) / modelsToRun.length) * 100);
    }

    setIsRunning(false);

    // const successCount = newResults.filter(r => r.status === 'success').length;
    // if (successCount > 0) {
    //   toast.success('AI audit completed', {
    //     description: `${successCount} model${successCount > 1 ? 's' : ''} executed successfully`
    //   });
    // } else {
    //   toast.error('AI audit failed', {
    //     description: 'All executions encountered errors'
    //   });
    // }
  };
  const handleRemoveFile = (fileId: string) => {
    setFile(prevFiles => prevFiles.filter(file => file.name !== fileId));
    toast.success('File removed successfully!', {
      description: `${fileId} is now removed successfully`
    })
    // setStatus({ message: 'File removed successfully!', type: 'success' });

  }
  const handleSavePreset = () => {
    if (!newPresetName.trim()) return;

    const newPreset = {
      id: `preset_${Date.now()}`,
      name: newPresetName,
      description: newPresetDescription,
      ...config,
      createdAt: new Date().toISOString(),
      usageCount: 0
    };

    setPresets(prev => [...prev, newPreset]);
    setShowPresetDialog(false);
    setNewPresetName('');
    setNewPresetDescription('');

    toast.success('Preset saved successfully', {
      description: `"${newPresetName}" is now available for reuse`
    });
  };

  const handleLoadPreset = (preset: any) => {
    setConfig({
      model: preset.model,
      temperature: preset.temperature,
      thinkingMode: preset.thinkingMode ?? false,
      systemPrompt: preset.systemPrompt,
      guardrails: preset.guardrails,
      structuredOutput: preset.structuredOutput
    });

    toast.success('Preset loaded', {
      description: `Configuration updated with "${preset.name}" settings`
    });
  };

  const handleDeletePreset = (presetId: string) => {
    setPresets(prev => prev.filter(p => p.id !== presetId));
    toast.success('Preset deleted');
  };

  // Mock data generators
  function generateMockOutput(modelId: string): string {
    // Sometimes return JSON output to test formatting
    const shouldReturnJSON = Math.random() > 0.7; // 30% chance for JSON output

    if (shouldReturnJSON) {
      const jsonOutput = {
        "analysis_type": "clinical_audit",
        "model_used": modelId,
        "timestamp": new Date().toISOString(),
        "patient_data": {
          "patient_id": "12345",
          "age": 45,
          "gender": "Female"
        },
        "diagnosis_codes": [
          {
            "code": "M79.3",
            "description": "Panniculitis, unspecified",
            "type": "primary",
            "accuracy": "correct",
            "confidence": 0.95
          },
          {
            "code": "Z87.891",
            "description": "Personal history of nicotine dependence",
            "type": "secondary",
            "accuracy": "appropriate",
            "confidence": 0.87
          }
        ],
        "procedure_codes": [
          {
            "code": "99213",
            "description": "Office visit, level 3",
            "accuracy": "appropriate",
            "justification": "Complexity level matches documentation",
            "confidence": 0.92
          }
        ],
        "quality_scores": {
          "documentation": Math.floor(Math.random() * 10) + 90,
          "coding_accuracy": Math.floor(Math.random() * 8) + 92,
          "clinical_reasoning": Math.floor(Math.random() * 12) + 88,
          "overall": Math.floor(Math.random() * 8) + 92
        },
        "compliance_status": "compliant",
        "findings": [
          "Chief complaint clearly documented",
          "HPI contains required 4+ elements",
          "Physical exam comprehensive and supports diagnosis",
          "Assessment and plan detailed and appropriate"
        ],
        "recommendations": [
          "Consider adding more specific anatomical location descriptors",
          "Ensure follow-up instructions are clearly documented",
          "Include patient education documentation"
        ],
        "risk_factors": {
          "coding_errors": "low",
          "documentation_gaps": "minimal",
          "billing_compliance": "high"
        },
        "summary": {
          "total_issues": 0,
          "critical_issues": 0,
          "warnings": 1,
          "billing_risk": "low",
          "overall_rating": "excellent"
        }
      };

      return JSON.stringify(jsonOutput);
    }

    // Return markdown formatted text output (existing logic)
    const outputs = {
      'gpt-4o': `**GPT-4o Analysis:**

Based on comprehensive chart review, I've identified key findings:

**Medical Coding Assessment:**
- Primary diagnosis: M79.3 (Panniculitis, unspecified) - Correctly coded
- Secondary conditions: Z87.891 (Personal history of nicotine dependence) - Appropriate
- Procedure codes: 99213 (Office visit, level 3) - Complexity justified

**Documentation Quality Score: 94/100**
- Chief complaint: Clearly documented ✓
- HPI: Contains required 4+ elements ✓
- Physical exam: Comprehensive, supports diagnosis ✓
- Assessment/Plan: Detailed and appropriate ✓

**Compliance Status: COMPLIANT**
All billing requirements met with excellent documentation standards.`,

      'gpt-4-turbo': `**GPT-4 Turbo Review:**

Chart analysis reveals high-quality clinical documentation:

**Coding Accuracy:**
- ICD-10: M79.3 correctly assigned as primary
- Supporting codes appropriately used
- CPT 99213 justified by complexity level

**Quality Metrics:**
- Documentation: 92/100
- Clinical reasoning: 89/100
- Coding accuracy: 96/100

**Key Strengths:**
✓ Thorough history documentation
✓ Appropriate differential consideration
✓ Clear treatment plan

**Minor recommendations:**
- Enhanced specificity in anatomical location
- More detailed follow-up timeline`,

      'claude-3-sonnet': `**Claude 3 Sonnet Assessment:**

The clinical documentation demonstrates excellent standards:

**Primary Findings:**
• Diagnosis coding (M79.3): Clinically appropriate and well-supported
• Documentation completeness: Exceeds minimum requirements
• Billing level justification: Clear correlation between service and complexity

**Quality Analysis:**
- Medical decision making: Appropriately complex
- Risk assessment: Properly documented
- Patient education: Clearly recorded

**Compliance Verification:**
✅ HIPAA-compliant documentation
✅ Regulatory requirements met
✅ Audit-ready quality standards

**Overall Score: 93/100**
Excellent clinical documentation with minimal enhancement opportunities.`,

      'claude-3-opus': `**Claude 3 Opus Comprehensive Review:**

This chart exemplifies high-quality clinical documentation practices:

**Diagnostic Excellence:**
- Primary diagnosis (M79.3): Accurately reflects clinical presentation
- Supporting documentation: Comprehensive and well-structured
- Differential considerations: Appropriately addressed

**Documentation Strengths:**
1. Complete history of present illness
2. Thorough physical examination findings
3. Evidence-based assessment
4. Clear therapeutic plan

**Regulatory Compliance:**
- Meets all CMS documentation requirements
- Supports billed service level (99213)
- Maintains appropriate medical necessity

**Quality Score: 95/100**
Outstanding documentation quality with excellent clinical reasoning.`,

      'gemini-pro': `**Gemini Pro Clinical Analysis:**

Advanced review of clinical documentation:

**Coding Validation:**
- M79.3 (Panniculitis): ✓ Clinically supported
- Z87.891 (Tobacco history): ✓ Relevant comorbidity
- 99213 E/M level: ✓ Complexity appropriate

**AI-Assisted Quality Metrics:**
- Semantic coherence: 96%
- Clinical logic flow: 94%
- Documentation completeness: 91%

**Pattern Recognition Results:**
• Standard terminology usage: Excellent
• Risk factor documentation: Complete
• Treatment protocol adherence: Appropriate

**Automated Compliance Check: PASSED**
All regulatory markers present and properly documented.`
    };

    return outputs[modelId] || outputs['gpt-4-turbo'];
  }

  function generateMockStructuredOutput(modelId: string): any {
    const baseStructure = {
      "model_used": modelId,
      "diagnosis_codes": [
        {
          "code": "M79.3",
          "description": "Panniculitis, unspecified",
          "type": "primary",
          "accuracy": "correct"
        }
      ],
      "procedure_codes": [
        {
          "code": "99213",
          "description": "Office visit, level 3",
          "accuracy": "appropriate"
        }
      ],
      "quality_scores": {
        "documentation": Math.floor(Math.random() * 10) + 90,
        "coding_accuracy": Math.floor(Math.random() * 8) + 92,
        "clinical_reasoning": Math.floor(Math.random() * 12) + 88,
        "overall": Math.floor(Math.random() * 8) + 92
      },
      "compliance_status": "compliant",
      "recommendations": [
        "Consider adding more specific location descriptors",
        "Ensure follow-up instructions are clearly documented"
      ]
    };

    // Add model-specific variations
    if (modelId.includes('claude')) {
      baseStructure.quality_scores.clinical_reasoning += 2;
    } else if (modelId.includes('gpt-4o')) {
      baseStructure.quality_scores.documentation += 3;
    }

    return baseStructure;
  }

  return (
    <Layout>
      <div className="flex h-screen overflow-hidden">
        {/* Main Content Area - Left Side */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex-shrink-0 p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-inter font-semibold text-2xl leading-[100%] tracking-[0]  flex items-center gap-2">
                  <Zap className="w-6 h-6 text-blue-600" />
                  TurfAI Studio
                </h1>
                <p className="font-inter font-normal text-[16px] leading-[20px] tracking-normal mt-2">
                  Advanced AI audit tool for testing prompts and analyzing clinical charts
                </p>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-auto p-6 space-y-6">
            {/* Prompt Input Area */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-inter font-normal text-[16px]">
                  <MessageSquare className="w-4 h-4" />
                  Prompt & Data Input
                </CardTitle>
                <CardDescription className='font-inter font-normal text-[16px]'>
                  Enter your prompt, chart data, and expected output (supports copy/paste and file upload)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  ref={promptTextareaRef}
                  value={combinedInput}
                  onChange={(e) => setCombinedInput(e.target.value)}
                  onKeyDown={handlePromptKeyDown}
                  placeholder={`Enter your complete prompt and data here. You can include:

PROMPT:
Your audit instructions here...

CHART_DATA:
{"patient_id": "12345", "diagnosis": "...", ...}

EXPECTED_OUTPUT (optional):
Expected result for comparison...`}
                  className="min-h-[450px] resize-y font-mono text-sm"
                  disabled={!hasPermission("prompts:create")}
                />
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {combinedInput.length} characters
                  </span>
                  {hasPermission("prompts:create") && (
                    <div className="flex gap-2">
                      {/* Hidden file input */}
                      <div className='flex justify-center align-center'>
                        {file?.map(file => (
                          <div key={file.id} className="file-item flex gap-2 items-center">
                            <div className="file-info">
                              {/* <span className="file-icon">📄</span> */}
                              <span className="file-name">
                                {file?.name}
                                {/* <span className="file-size">({formatFileSize(file.size)})</span> */}
                              </span>
                            </div>
                            <button
                              className="remove-btn"
                              onClick={() => handleRemoveFile(file?.name)}
                              title="Remove file"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <input
                        type="file"
                        accept=".txt,.json,.md,.csv,.xml,.yaml,.yml"
                        onChange={(event) => {
                          if (hasPermission("prompts:create")) {
                            handleFileUpload(event);
                          }
                          else {
                            toast.error('You do not have permission to upload files.');
                          }
                        }}
                        style={{ display: 'none' }}
                        id="file-upload"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (hasPermission("prompts:create")) {
                            document.getElementById('file-upload')?.click();
                          } else {
                            toast.error('You do not have permission to upload files.');
                          }
                        }}
                        disabled={isUploading}>
                        {isUploading ? (
                          <>
                            <Activity className="w-3 h-3 mr-1 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-3 h-3 mr-1" />
                            Upload File
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopyToClipboard}
                        disabled={!combinedInput.trim()}
                      >
                        {copiedStates['input-copy'] ? (
                          <Check className="w-3 h-3 text-green-600" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </Button>
                      <Button
                        onClick={handleRunAudit}
                        disabled={!canRun || isRunning}
                        className="bg-blue-600 hover:bg-blue-700"
                        size="sm"
                      >
                        {isRunning ? (
                          <>
                            <Activity className="w-3 h-3 mr-1 animate-spin" />
                            Running...
                          </>
                        ) : (
                          <>
                            <Play className="w-3 h-3 mr-1" />
                            {comparisonEnabled ? 'Run Comparison' : 'Run'}
                          </>
                        )}
                      </Button>
                    </div>)}
                </div>
              </CardContent>
            </Card>

            {/* Progress Indicator */}
            {isRunning && (
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 animate-spin" />
                      <span className="text-sm font-medium">
                        {comparisonEnabled ? 'Running comparison audit...' : 'Running AI audit...'}
                      </span>
                    </div>
                    <Progress value={runProgress} className="w-full" />
                    <p className="text-xs text-muted-foreground">
                      {comparisonEnabled
                        ? `Comparing ${selectedModel?.name} vs ${selectedComparisonModel?.name}`
                        : `Processing with ${selectedModel?.name}`
                      } • {runProgress.toFixed(0)}% complete
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Results Section */}
            {results.length > 0 && (
              <div className="space-y-6">
                {comparisonEnabled && results.length > 1 ? (
                  /* Comparison Results */
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <GitCompare className="w-4 h-4" />
                        Model Comparison Results
                      </CardTitle>
                      <CardDescription>
                        Side-by-side comparison of outputs from different AI models
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {results?.map((result, index) => (
                          <div key={result.id} className="space-y-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">{result.modelName}</Badge>
                                <Badge variant={result.status === 'success' ? 'default' : 'destructive'}>
                                  {result.status}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="text-sm text-muted-foreground">
                                  {result.duration.toFixed(1)}s • {result.tokensUsed} tokens • ${result.cost.toFixed(3)}
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleCopyOutput(result.output, result.id)}
                                    className="h-6 w-6 p-0"
                                  >
                                    {copiedStates[`result-${result.id}`] ? (
                                      <Check className="w-3 h-3 text-green-600" />
                                    ) : (
                                      <Copy className="w-3 h-3" />
                                    )}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDownloadOutput(result.output, result.modelName, result.id)}
                                    className="h-6 w-6 p-0"
                                  >
                                    <Download className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            </div>

                            <ScrollArea className="h-80 w-full border rounded-md p-4 bg-muted/30">
                              <div
                                ref={(el) => {
                                  if (el) aiOutputRefs.current[result.id] = el;
                                }}
                                tabIndex={0}
                                onKeyDown={(e) => handleOutputKeyDown(e, result.id)}
                                className="outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-30 rounded"
                              >
                                <FormattedOutput content={result.output} />
                              </div>
                            </ScrollArea>

                            {/* Execution Log */}
                            {/* <div className="space-y-2">
                              <Label className="text-xs font-medium">Execution Log</Label>
                              <ScrollArea className="h-24 w-full border rounded-md p-2 bg-gray-50">
                                <div className="space-y-1">
                                  {result.executionLog.map((log, logIndex) => (
                                    <div key={logIndex} className="text-xs font-mono text-gray-600">
                                      {log}
                                    </div>
                                  ))}
                                </div>
                              </ScrollArea>
                            </div> */}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  /* Single Result */
                  results?.map(result => (
                    <Card key={result.id}>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Zap className="w-4 h-4" />
                          Audit Results
                          <Badge variant={result.status === 'success' ? 'default' : 'destructive'}>
                            {result.status}
                          </Badge>
                          <Badge variant="outline">{result.modelName}</Badge>
                        </CardTitle>
                        <CardDescription>
                          Run completed in {result.duration.toFixed(1)}s
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {result.status === 'error' && result.errors && (
                          <Alert>
                            <AlertTriangle className="w-4 h-4" />
                            <AlertDescription>
                              <strong>Errors:</strong>
                              <ul className="list-disc list-inside mt-1">
                                {result.errors.map((error, index) => (
                                  <li key={index}>{error}</li>
                                ))}
                              </ul>
                            </AlertDescription>
                          </Alert>
                        )}

                        {result.warnings && (
                          <Alert>
                            <AlertTriangle className="w-4 h-4" />
                            <AlertDescription>
                              <strong>Warnings:</strong>
                              <ul className="list-disc list-inside mt-1">
                                {result.warnings.map((warning, index) => (
                                  <li key={index}>{warning}</li>
                                ))}
                              </ul>
                            </AlertDescription>
                          </Alert>
                        )}

                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">AI Output</Label>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCopyOutput(result.output, result.id)}
                              >
                                {copiedStates[`result-${result.id}`] ? (
                                  <Check className="w-3 h-3 text-green-600" />
                                ) : (
                                  <Copy className="w-3 h-3" />
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadOutput(result.output, result.modelName, result.id)}
                              >
                                <Download className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                          <ScrollArea className="h-80 w-full border rounded-md p-4 bg-muted/30">
                            <FormattedOutput content={result.output} />
                          </ScrollArea>
                        </div>

                        {config.structuredOutput && result.structured && (
                          <div className="space-y-4">
                            <Label className="text-sm font-medium">Structured Output</Label>
                            <ScrollArea className="h-48 w-full border rounded-md p-4 bg-muted/30">
                              <pre className="text-xs">{JSON.stringify(result.structured, null, 2)}</pre>
                            </ScrollArea>
                          </div>
                        )}

                        {/* Execution Log - Collapsible */}
                        <Collapsible
                          open={executionLogExpanded[result.id] || false}
                          onOpenChange={(open) => setExecutionLogExpanded(prev => ({ ...prev, [result.id]: open }))}
                        >
                          {/* <CollapsibleTrigger asChild>
                            <div className="cursor-pointer hover:bg-muted/50 transition-colors rounded-md p-2 -m-2">
                              <Label className="text-sm font-medium flex items-center justify-between cursor-pointer">
                                <div className="flex items-center gap-2">
                                  <Monitor className="w-3 h-3" />
                                  Execution Log
                                </div>
                                <ChevronDown className={`w-4 h-4 transform transition-transform duration-200 ${
                                  executionLogExpanded[result.id] ? 'rotate-180' : ''
                                }`} />
                              </Label>
                            </div>
                          </CollapsibleTrigger> */}
                          <CollapsibleContent className="animate-fade-in">
                            <ScrollArea className="h-32 w-full border rounded-md p-3 bg-gray-50 mt-2">
                              <div className="space-y-1">
                                {result?.executionLog?.map((log, logIndex) => (
                                  <div key={logIndex} className="text-xs font-mono text-gray-600">
                                    {log}
                                  </div>
                                ))}
                              </div>
                            </ScrollArea>
                          </CollapsibleContent>
                        </Collapsible>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}

            {!isRunning && results.length === 0 && (
              <div className="text-center py-12">
                <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-6 h-6 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Run</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Enter your prompt and data above, configure settings on the right, then run your audit
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Settings Sidebar - Right Side */}
        <div className="w-72 border-l border-gray-200 bg-gray-50 overflow-auto">
          <div className="p-4 space-y-6">
            {/* Model Configuration */}
            <Card>
              <CardHeader className="pb-2.5">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Brain className="w-4 h-4" />
                  Model Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs mb-2">Primary Model</Label>
                  <Select value={config.model} onValueChange={(value) => handleConfigChange('model', value)}>
                    <SelectTrigger className="text-sm">
                      <SelectValue placeholder="Select a model" />
                    </SelectTrigger>
                    <SelectContent>
                      {promptsList?.map((model) => (
                        <SelectItem key={model.id} value={model.id}>
                          <div className="flex flex-col">
                            <span className="font-medium text-xs">{model.name}</span>
                            {/* <span className="text-xs text-muted-foreground">{model.provider}</span> */}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-xs font-medium">Thinking Mode</Label>
                    <p className="text-xs text-muted-foreground">Enable extended reasoning</p>
                  </div>
                  <Switch
                    checked={config.thinkingMode}
                    onCheckedChange={(checked) => handleConfigChange('thinkingMode', checked)}
                    disabled={isRunning || thinkingUiLocked || !modelThinkingModeEnabled}
                  />
                </div>

                {config.thinkingMode && (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs mb-2">Thinking Level</Label>
                      <Select
                        value={config.thinkingLevel || ''}
                        onValueChange={(value) => handleConfigChange('thinkingLevel', value)}
                        disabled={isRunning || thinkingUiLocked || !normalizedThinkingLevels}
                      >
                        <SelectTrigger className="text-sm">
                          <SelectValue placeholder={normalizedThinkingLevels ? 'Select level' : 'N/A'} />
                        </SelectTrigger>
                        <SelectContent>
                          {(normalizedThinkingLevels ?? []).map((lvl) => (
                            <SelectItem key={lvl} value={lvl}>
                              {lvl.charAt(0).toUpperCase() + lvl.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs mb-2">Thinking Budget</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder={isBudgetNumeric ? 'Enter a budget value' : 'N/A'}
                        value={thinkingBudgetInput}
                        disabled={isRunning || thinkingUiLocked || !modelThinkingModeEnabled || !isBudgetNumeric}
                        onChange={(e) => {
                          setThinkingBudgetError(null);
                          const raw = e.target.value.replace(/\D/g, '');
                          setThinkingBudgetInput(raw);
                        }}
                        onBlur={() => {
                          if (!isBudgetNumeric) return; // field is disabled for N/A models
                          const raw = thinkingBudgetInput.trim();
                          if (raw === '') {
                            handleConfigChange('thinkingBudget', modelBudgetMin);
                            setThinkingBudgetInput(String(modelBudgetMin));
                            setThinkingBudgetError(null);
                            return;
                          }
                          const num = parseInt(raw, 10);
                          if (Number.isNaN(num)) {
                            setThinkingBudgetError('Enter a valid number');
                            setThinkingBudgetInput(String(config.thinkingBudget));
                            return;
                          }

                          if (isBudgetNumeric) {
                            if (num < modelBudgetMin || num > modelBudgetMax) {
                              // Keep the invalid text visible so submit-time validation blocks the API call.
                              setThinkingBudgetError(
                                `Please enter a value between ${modelBudgetMin.toLocaleString()} and ${modelBudgetMax.toLocaleString()}`
                              );
                              return;
                            }
                            handleConfigChange('thinkingBudget', num);
                            setThinkingBudgetInput(String(num));
                          } else {
                            // N/A budgets: don't enforce range; clamp to backend-safe defaults.
                            const next = Math.max(BUDGET_MIN, Math.min(BUDGET_MAX, num));
                            handleConfigChange('thinkingBudget', next);
                            setThinkingBudgetInput(String(next));
                          }
                          setThinkingBudgetError(null);
                        }}
                        className={`text-sm ${thinkingBudgetError ? 'border-destructive' : ''}`}
                      />
                      {thinkingBudgetError ? (
                        <p className="text-xs text-destructive mt-1">{thinkingBudgetError}</p>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-1">
                          Range: {isBudgetNumeric ? `${modelBudgetMin.toLocaleString()} – ${modelBudgetMax.toLocaleString()}` : 'N/A'}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-xs font-medium">Model Comparison</Label>
                    <p className="text-xs text-muted-foreground">Run with two models</p>
                  </div>
                  <Switch
                    checked={comparisonEnabled}
                    onCheckedChange={setComparisonEnabled}
                  />
                </div> */}

                {comparisonEnabled && (
                  <div>
                    <Label className="text-xs">Comparison Model</Label>
                    <Select value={comparisonModel} onValueChange={setComparisonModel}>
                      <SelectTrigger className="text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AI_MODELS.filter(m => m.id !== config.model).map(model => (
                          <SelectItem key={model.id} value={model.id}>
                            <div className="flex flex-col">
                              <span className="font-medium text-xs">{model.name}</span>
                              <span className="text-xs text-muted-foreground">{model.provider}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="text-xs mb-2">Temperature: {config.temperature}</Label>
                  <Slider
                    value={[config.temperature]}
                    onValueChange={(value) => handleConfigChange('temperature', value[0])}
                    max={1}
                    min={0}
                    step={0.1}
                    className="mt-1"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>Focused</span>
                    <span>Creative</span>
                  </div>
                </div>

                {/* <div>
                  <Label className="text-xs">System Prompt</Label>
                  <Textarea
                    value={config.systemPrompt}
                    onChange={(e) => handleConfigChange('systemPrompt', e.target.value)}
                    placeholder="Enter system instructions..."
                    className="min-h-16 text-xs"
                  />
                </div> */}

                {/* <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-xs font-medium">Structured Output</Label>
                    <p className="text-xs text-muted-foreground">JSON schema</p>
                  </div>
                  <Switch
                    checked={config.structuredOutput}
                    onCheckedChange={(checked) => handleConfigChange('structuredOutput', checked)}
                  />
                </div> */}
              </CardContent>
            </Card>

            {/* Security Guardrails */}
            {/* <Card>
              <Collapsible open={guardrailsExpanded} onOpenChange={setGuardrailsExpanded}>
                <CollapsibleTrigger asChild>
                  <CardHeader className="pb-2.5 cursor-pointer hover:bg-muted/50 transition-colors">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Security Guardrails
                      </div>
                      <ChevronDown className={`w-4 h-4 transform transition-transform duration-200 ${
                        guardrailsExpanded ? 'rotate-180' : ''
                      }`} />
                    </CardTitle>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent className="animate-fade-in">
                  <CardContent className="pt-0">
                    <div className="space-y-3">
                      {GUARDRAIL_CATEGORIES.map(category => (
                        <div key={category.id} className="flex items-center justify-between">
                          <div>
                            <Label className="text-xs font-medium">
                              {category.name}
                            </Label>
                            <p className="text-xs text-muted-foreground">
                              {category.description}
                            </p>
                          </div>
                          <Switch
                            checked={config.guardrails[category.id]}
                            onCheckedChange={(checked) => handleGuardrailChange(category.id, checked)}
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card> */}
          </div>
        </div>
      </div>
    </Layout>
  );
}